import React from 'react';
import PropTypes from 'prop-types';
import { t } from '../../../../services/i18nService';
import styles from './textEntry.scss';

import marketplacePropertiesService from '../../../../services/MarketplacePropertiesService';
import stringUtils from '../../../../qti/utils/StringUtils';
import Constants from '../../../../qti/Constants';
import utilService from '../../../../services/UtilService';
import IntervalSaver from '../../../../services/IntervalSaver';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../../../services/a11yIIBScreenReaderService';
import { getQuestionContentIdForA11y } from './textEntryUtils';

class TextEntryNumericInput extends React.Component {
    static propTypes = {
        callback: PropTypes.func.isRequired, // callback(responseIdentifier, [simpleChoice identifiers])
        defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        maxLength: PropTypes.number,
        model: PropTypes.object.isRequired, // model taken from responseModels related to this interaction
        handleInteractionReady: PropTypes.func.isRequired,
        isReadOnly: PropTypes.bool,
        size: PropTypes.number,
        orderNumber: PropTypes.string,
        services: PropTypes.object,
        questionModel: PropTypes.object,
        isActive: PropTypes.bool,
        isFlagged: PropTypes.bool,
        onChangeCallback: PropTypes.func,
        expandableInputStyles: PropTypes.object,
    };

    constructor(props) {
        super(props);

        this.instanceId = stringUtils.getUID();
        this.errorMessageId = `${this.instanceId}-error`;
        this.expectedDecimals = utilService.getIntOr(props.model.expectedDecimals, 99);
        this.allowedKeyCodes = Constants.allowedKeyCodesNumericInput;
        this.isDecimalPointEnforced = marketplacePropertiesService.isNumericDecimalPointEnforced();
        this.decimalSeparator = this.isDecimalPointEnforced ? '.' : '.,';

        this.state = {
            decimalsError: false,
            formatError: false,
            showInputError: false,
            missingEnforcedSeparator: false,
            decimalPointButNoDecimalsError: false,
            isFocused: false,
        };
    }

    componentDidMount() {
        this.props.handleInteractionReady(true);
        this.lastChangedAt = new Date().getTime();

        this.smartSaver = new IntervalSaver(this.props.defaultValue, (value) => {
            this.props.callback(this.props.model.identifier, [value]);
        });
        this.timerHandle = setInterval(this.runValidation, 500);
    }

    componentWillUnmount() {
        this.smartSaver.saveAndStop();
        clearInterval(this.timerHandle);
    }

    /**
     * @param {String} stringValue
     * @param {Number} expectedDecimals
     * @return {Number || String} A parsed number or empty string.
     */
    getParsedNumber(stringValue, expectedDecimals) {
        let value;
        if (expectedDecimals === 0) {
            value = Number.parseInt(stringValue, 10);
            value = isNaN(value) ? '' : value;
        } else {
            value = utilService.parseFloatSafely(stringValue);
            value = isNaN(value) ? '' : value;
        }
        return value;
    }

    isValid(value) {
        const validValueRegExp = new RegExp(`^-?\\d+([${this.decimalSeparator}]\\d+)?$`);
        return validValueRegExp.test(value);
    }

    isValidWhileTyping(value) {
        const validWhileTypingRegExp = new RegExp(`^(-)?([0-9]+([${this.decimalSeparator}]([0-9]+)?)?)?$`);
        return validWhileTypingRegExp.test(value);
    }

    hasDecimalPointButNoDecimals(value) {
        const endsWithDecimalPointRegExp = new RegExp(`([${this.decimalSeparator}])$`);
        return endsWithDecimalPointRegExp.test(value);
    }

    hasIsDecimalPointEnforcedAndDecimalPoint(value) {
        return !!value && this.isDecimalPointEnforced && !(new RegExp(this.decimalSeparator).test(value));
    }

    setValue(value) {
        this.inputElement.value = value;
    }

    runValidation = () => {
        try {
            const ts = new Date().getTime();
            if (ts < this.lastChangedAt + 3000) {
                return;
            }

            if (!this.inputElement) {
                return;
            }

            const { value } = this.inputElement;
            const validations = this.validateValue(value);
            this.setState(validations);
        } catch (error) {
            console.error(error);
        }
    };

    validateValue(value) {
        const validations = {
            formatError: false,
            decimalsError: false,
            decimalPointButNoDecimalsError: false,
            missingEnforcedSeparator: false,
        };

        if (value === '') {
            return validations;
        }

        validations.decimalPointButNoDecimalsError = this.hasDecimalPointButNoDecimals(value);
        if (validations.decimalPointButNoDecimalsError) {
            return validations;
        }

        validations.formatError = !this.isValid(value);
        if (validations.formatError) {
            return validations;
        }

        validations.missingEnforcedSeparator = this.hasIsDecimalPointEnforcedAndDecimalPoint(value);
        if (validations.missingEnforcedSeparator) {
            return validations;
        }

        let decimals = 0;
        const splitReg = new RegExp(`[${this.decimalSeparator}]`);
        const parts = value.split(splitReg);
        if (parts.length > 1) {
            decimals = parts[parts.length - 1].length;
        }
        validations.decimalsError = decimals > this.expectedDecimals;

        return validations;
    }

    hasErrors(data) {
        return data.formatError || data.decimalsError || data.decimalPointButNoDecimalsError || data.missingEnforcedSeparator;
    }

    onChange = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const { model, onChangeCallback } = this.props;
        const { value } = this.inputElement;
        const validations = this.validateValue(value);
        if (this.hasErrors(this.state) && !this.hasErrors(validations)) {
            this.setState(validations);
        }

        this.lastChangedAt = new Date().getTime();

        // Parse value and save: use delayed save if a11y screen reader support is enabled, otherwise save immediately
        const parsedValue = this.getParsedNumber(value, this.expectedDecimals);

        if (a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported()) {
            // Delayed save: allow IntervalSaver to save after delay (to avoid announcing on every keystroke)
            this.smartSaver.currentValue = parsedValue;
            this.smartSaver.lastSetValueTS = new Date().getTime();
        } else {
            // Immediate save: callback is invoked right away
            this.smartSaver.setValue(parsedValue);
        }

        if (model.expandAutomatically && onChangeCallback) {
            onChangeCallback(this.inputElement.value);
        }
    };

    onKeyDown = (event) => {
        const { value } = this.inputElement;
        const selectionStart = this.inputElement.selectionStart;
        const selectionEnd = this.inputElement.selectionEnd;
        const string = value.slice(0, selectionStart) + event.key + value.slice(selectionStart + Math.abs(selectionEnd - selectionStart));
        const selectAllAction = event.keyCode === 65 && (event.ctrlKey === true || event.metaKey === true);
        const allowedKeyCodesArray = Object.values(this.allowedKeyCodes);

        // Allow Home and End keys for cursor navigation when v4 is enabled
        const isHomeOrEndKey = event.keyCode === Constants.navigationKeyCodes.HOME_KEY
            || event.keyCode === Constants.navigationKeyCodes.END_KEY;
        const isV4Supported = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v4);
        const shouldAllowHomeEnd = isHomeOrEndKey && isV4Supported;

        const isTyping = !allowedKeyCodesArray.includes(event.keyCode) && !selectAllAction && !shouldAllowHomeEnd;
        if (isTyping && !this.isValidWhileTyping(string)) {
            this.setState({
                showInputError: true,
            });
            event.preventDefault();
            event.stopPropagation();
            return false;
        }

        this.setState({
            showInputError: false,
        });
        return true;
    };

    onFocus = () => {
        if (a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported()) {
            this.setState({ isFocused: true });
        }
        this.navigateToScorableItem();
    };

    onBlur = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const { value } = this.inputElement;
        const validations = this.validateValue(value);
        const stateUpdate = {
            ...validations,
            showInputError: false,
        };
        if (a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported()) {
            stateUpdate.isFocused = false;
        }
        this.setState(stateUpdate);
        this.smartSaver.setValue(
            this.getParsedNumber(value, this.expectedDecimals)
        );
    };

    navigateToScorableItem = () => {
        const { services, model, questionModel } = this.props;
        services.scorableItemNumberingService.navigateToScorableItem(questionModel.contentRevisionId, model.identifier, undefined, true);
    };

    getInvalidInputMessage() {
        return this.isDecimalPointEnforced
            ? <span>
                { t('interactions.numericEntry.pointDelimiterError') }
            </span>
            : <span className={styles.inputValidationErrorMessage}>
                <i className="fa fa-times-circle" aria-hidden="true" />
                {t('interactions.mathEntry.inputValidationErrorMessage')}
            </span>;
    }

    getErrorMessage() {
        const {
            decimalPointButNoDecimalsError,
            decimalsError,
            formatError,
        } = this.state;

        if (this.state.missingEnforcedSeparator && this.isDecimalPointEnforced) {
            return t('interactions.numericEntry.pointDelimiterError');
        }

        if (formatError) {
            return t('interactions.mathEntry.formatError');
        }

        if (decimalsError) {
            return t('interactions.mathEntry.decimalsError');
        }

        if (decimalPointButNoDecimalsError) {
            return t('interactions.mathEntry.decimalPointButNoDecimalsError');
        }

        return null;
    }

    getInputClassList() {
        const { showInputError } = this.state;
        const { isActive, isFlagged } = this.props;

        const className = ['textEntryInteractionValue'];
        if (isActive) {
            className.push('active');
        }
        if (isFlagged) {
            className.push('flagged');
        }
        if (showInputError) {
            className.push(styles.inputInvalidContent);
        }

        return className;
    }

    getRootClassList() {
        const classList = [];
        if (this.hasErrors(this.state) || this.state.showInputError) {
            classList.push(styles.validationError);
        }

        if (this.isDecimalPointEnforced) {
            classList.push(styles.plainErrorWrapper);
        }

        return classList;
    }

    renderErrorMessages(errorMessage) {
        const { showInputError } = this.state;

        if (!errorMessage && !showInputError) {
            return null;
        }

        return (<span id={this.errorMessageId} className={styles.message} role="alert">
            { errorMessage || null }
            { errorMessage && showInputError && <div style={{ marginTop: '1em' }} />}
            { showInputError && this.getInvalidInputMessage() }
        </span>);
    }

    render() {
        const {
            orderNumber,
            model,
            expandableInputStyles,
            defaultValue,
            isReadOnly,
            maxLength,
            size,
            questionModel,
        } = this.props;

        const isA11ySupported = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported();
        const isA11yV4Supported = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v4);
        const isA11yV5Enabled = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v5);
        const labelKey = isA11ySupported
            ? t('interactions.inputEntry.textInputFieldAriaLabel')
            : t('interactions.inputEntry.inputFieldAriaLabel');
        const labelMessage = isA11ySupported && !this.state.isFocused ? undefined : labelKey;
        const questionContentId = isA11yV5Enabled ? getQuestionContentIdForA11y(questionModel, model) : undefined;
        const ariaLabelledBy = isA11yV5Enabled ? questionContentId : undefined;
        const inputStyle = (model.expandAutomatically && expandableInputStyles) ? expandableInputStyles : {};
        const inputClassName = this.getInputClassList();
        const errorMessage = this.getErrorMessage();
        const rootClassList = this.getRootClassList();
        const hasError = this.hasErrors(this.state) || this.state.showInputError;
        const ariaDescribedBy = isA11yV4Supported && hasError ? this.errorMessageId : undefined;

        return (
            <span className={rootClassList.join(' ')} dir="ltr">
                { this.renderErrorMessages(errorMessage) }

                <label htmlFor={this.instanceId}>
                    <input
                        aria-invalid={errorMessage ? 'true' : 'false'}
                        aria-describedby={ariaDescribedBy}
                        id={this.instanceId}
                        autoComplete="off"
                        className={inputClassName.join(' ')}
                        defaultValue={defaultValue}
                        disabled={isReadOnly}
                        maxLength={maxLength}
                        name={this.instanceId}
                        onBlur={this.onBlur}
                        onKeyDown={this.onKeyDown}
                        onChange={this.onChange}
                        onFocus={this.onFocus}
                        ref={(element) => {
                            this.inputElement = element;
                        }}
                        size={size}
                        spellCheck="false"
                        type="text"
                        placeholder={orderNumber}
                        style={inputStyle}
                        aria-label={labelMessage}
                        aria-labelledby={ariaLabelledBy}
                    />
                </label>
            </span>
        );
    }
}

export default TextEntryNumericInput;
